/**
 * Site Provider
 *
 * Provides site state management via React Context.
 * Handles loading site configuration from the API and managing error states.
 * Also manages site keys for QR code URL validation.
 *
 * Requirements: 2.1, 6.1, 6.2, 4.1, 4.2, 4.3, 4.4, 4.5, 5.1, 5.2, 5.3
 */

import { useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { SiteContext, initialSiteState, type SiteState } from './SiteContext';
import {
  fetchSiteConfig,
  fetchSiteKeys,
  createSiteKey,
  SiteNotFoundError,
} from '../api/siteApiClient';
import { toSite } from '../models/siteAdapter';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import { useAnalytics } from '../../analytics';

interface SiteProviderProps {
  readonly children: ReactNode;
}

/**
 * SiteProvider component that manages site state and provides it to children.
 *
 * Implements:
 * - State management for site, isLoading, and error
 * - loadSite function with API client integration
 * - clearSite function to reset state
 * - Key management: refreshKeys, createKey, ensureKey
 * - Error handling for SiteNotFoundError (Requirement 6.1) and SiteApiError (Requirement 6.2)
 */
export function SiteProvider({ children }: SiteProviderProps): JSX.Element {
  const [state, setState] = useState<SiteState>(initialSiteState);
  const { getAccessToken } = useAuth();
  const { track } = useAnalytics();
  
  // Use ref to track current siteId for key operations
  const currentSiteIdRef = useRef<string | null>(null);
  
  // Track in-flight key creation to prevent concurrent duplicate requests
  const keyCreationPromiseRef = useRef<Promise<string> | null>(null);
  
  // Track in-flight key fetch to wait for it before creating keys
  // This is set before the fetch starts and cleared after it completes
  const keyFetchPromiseRef = useRef<Promise<readonly string[]> | null>(null);
  
  // Resolver for waiting on key fetch to start (handles race between loadSite and ensureKey)
  const pendingKeyFetchRef = useRef<{
    promise: Promise<readonly string[]>;
    resolve: (keys: readonly string[]) => void;
  } | null>(null);

  /**
   * Fetches keys for the current site and updates state.
   * Internal helper used by loadSite and refreshKeys.
   * Returns the fetched keys array.
   */
  const fetchKeysForSite = useCallback(
    async (siteId: string): Promise<readonly string[]> => {
      setState((prev) => ({ ...prev, isKeysLoading: true, keysError: null }));

      const fetchPromise = (async (): Promise<readonly string[]> => {
        try {
          const config = getAppConfig();
          const response = await fetchSiteKeys(siteId, {
            apiHost: config.apiHost,
            getAccessToken,
          });
          setState((prev) => ({
            ...prev,
            keys: response.keys,
            isKeysLoading: false,
            keysError: null,
          }));
          return response.keys;
        } catch {
          // Requirement 5.1: IF fetching keys fails, display an error message
          setState((prev) => ({
            ...prev,
            isKeysLoading: false,
            keysError: { type: 'fetch_failed', message: 'Failed to load site keys' },
          }));
          return [];
        } finally {
          keyFetchPromiseRef.current = null;
        }
      })();

      keyFetchPromiseRef.current = fetchPromise;
      return fetchPromise;
    },
    [getAccessToken]
  );

  /**
   * Loads site configuration from the API.
   * After successful site load, also fetches site keys.
   *
   * Requirement 4.2: WHEN a site is loaded, THE system SHALL fetch the site keys
   * Requirement 6.1: IF the API returns a 404 status, sets error with type 'not_found'
   * Requirement 6.2: IF the API returns any other error status, sets error with type 'api_error'
   */
  const loadSite = useCallback(
    async (siteId: string): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      currentSiteIdRef.current = siteId;
      
      // Create a pending fetch promise that ensureKey can wait on
      // This handles the race condition where ensureKey is called before fetchKeysForSite starts
      let resolvePendingFetch: (keys: readonly string[]) => void;
      const pendingFetchPromise = new Promise<readonly string[]>((resolve) => {
        resolvePendingFetch = resolve;
      });
      pendingKeyFetchRef.current = {
        promise: pendingFetchPromise,
        resolve: resolvePendingFetch!,
      };

      try {
        const config = getAppConfig();
        const response = await fetchSiteConfig(siteId, {
          apiHost: config.apiHost,
          getAccessToken,
        });
        const site = toSite(response);
        setState((prev) => ({ ...prev, site, isLoading: false, error: null }));
        
        // Requirement 4.2: Fetch keys after successful site load
        const keys = await fetchKeysForSite(siteId);
        
        // Resolve the pending fetch promise with the fetched keys
        pendingKeyFetchRef.current?.resolve(keys);
        pendingKeyFetchRef.current = null;
      } catch (error) {
        // Clear pending fetch on error
        pendingKeyFetchRef.current?.resolve([]);
        pendingKeyFetchRef.current = null;
        
        if (error instanceof SiteNotFoundError) {
          // Requirement 6.1: 404 status produces 'not_found' error
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: { type: 'not_found', siteId },
          }));
        } else {
          // Requirement 6.2: Other errors produce 'api_error'
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: { type: 'api_error', message: 'Failed to load site' },
          }));
        }
      }
    },
    [getAccessToken, fetchKeysForSite]
  );

  /**
   * Clears the current site state, resetting to initial values.
   */
  const clearSite = useCallback((): void => {
    currentSiteIdRef.current = null;
    keyCreationPromiseRef.current = null;
    keyFetchPromiseRef.current = null;
    pendingKeyFetchRef.current = null;
    setState(initialSiteState);
  }, []);

  /**
   * Refreshes the site keys from the API.
   *
   * Requirement 4.3: THE Site_Context SHALL provide a function to refresh site keys
   * Requirement 5.3: WHEN a key operation fails, THE system SHALL allow the user to retry
   */
  const refreshKeys = useCallback(async (): Promise<void> => {
    const siteId = currentSiteIdRef.current;
    if (!siteId) {
      return;
    }
    await fetchKeysForSite(siteId);
  }, [fetchKeysForSite]);

  /**
   * Creates a new key for the current site.
   * Returns the existing key if one already exists (prevents duplicate key creation).
   * Waits for any in-flight key fetch to complete before deciding.
   * Uses a ref to prevent concurrent duplicate API calls.
   *
   * Requirement 4.4: THE Site_Context SHALL provide a function to create a new key and update the state
   * Requirement 4.5: WHILE keys are being created, THE Site_Context SHALL indicate the loading state
   * Requirement 5.2: IF creating a key fails, display an error message
   */
  const createKey = useCallback(async (): Promise<string> => {
    const siteId = currentSiteIdRef.current;
    if (!siteId) {
      throw new Error('No site loaded');
    }

    // Return existing key if one already exists - prevents duplicate key creation
    if (state.keys.length > 0) {
      return state.keys[0];
    }

    // If a key fetch is in progress, wait for it and check if keys were found
    const fetchPromise = keyFetchPromiseRef.current;
    if (fetchPromise) {
      const fetchedKeys = await fetchPromise;
      if (fetchedKeys.length > 0) {
        return fetchedKeys[0];
      }
    }
    
    // If loadSite is in progress but fetchKeysForSite hasn't started yet,
    // wait for the pending fetch promise
    const pendingFetch = pendingKeyFetchRef.current;
    if (pendingFetch) {
      const fetchedKeys = await pendingFetch.promise;
      if (fetchedKeys.length > 0) {
        return fetchedKeys[0];
      }
    }

    // If a key creation is already in flight, return that promise to prevent duplicates
    if (keyCreationPromiseRef.current) {
      return keyCreationPromiseRef.current;
    }

    setState((prev) => ({ ...prev, isKeysLoading: true, keysError: null }));

    const createKeyPromise = (async (): Promise<string> => {
      try {
        const config = getAppConfig();
        const response = await createSiteKey(siteId, {
          apiHost: config.apiHost,
          getAccessToken,
        });
        
        // Add the new key to the keys array
        setState((prev) => ({
          ...prev,
          keys: [...prev.keys, response.key],
          isKeysLoading: false,
          keysError: null,
        }));
        
        return response.key;
      } catch {
        // Requirement 5.2: IF creating a key fails, display an error message
        // Requirement 9.2: Track key creation failure with SiteId
        track('Pocket Admin Key Creation Failed', { SiteId: siteId });
        setState((prev) => ({
          ...prev,
          isKeysLoading: false,
          keysError: { type: 'create_failed', message: 'Failed to create site key' },
        }));
        throw new Error('Failed to create site key');
      } finally {
        // Clear the in-flight promise when done
        keyCreationPromiseRef.current = null;
      }
    })();

    keyCreationPromiseRef.current = createKeyPromise;
    return createKeyPromise;
  }, [getAccessToken, state.keys, track]);

  /**
   * Ensures a key exists for the current site.
   * Returns the first existing key, or creates a new one if none exist.
   *
   * Requirement 3.2: IF multiple keys exist, use the first key in the list
   * Requirement 3.3: IF no keys exist, first create a key before generating the URL
   */
  const ensureKey = useCallback(async (): Promise<string> => {
    // Return first existing key if available
    if (state.keys.length > 0) {
      return state.keys[0];
    }
    
    // Create a new key if none exist
    return createKey();
  }, [state.keys, createKey]);

  const contextValue = useMemo(
    () => ({
      ...state,
      loadSite,
      clearSite,
      refreshKeys,
      createKey,
      ensureKey,
    }),
    [state, loadSite, clearSite, refreshKeys, createKey, ensureKey]
  );

  return (
    <SiteContext.Provider value={contextValue}>{children}</SiteContext.Provider>
  );
}
